import { useRef, useState } from 'react'
import { Alert, Button, Checkbox, Descriptions, Drawer, Form, InputNumber, Radio, Select, Space, Typography } from 'antd'
import { cfdApi } from '../services/api'
import type { AccountAuth, MaintenanceTarget, OperationCommand } from '../types/cfdWorkspace'
import { maintenanceCommands, MaintenanceValues, newRequestId, rememberSubmissions } from '../utils/cfdCommands'

export default function CfdMaintenanceDrawer({ tokenId, accountName, authMode, target, healthy, onClose, onSubmitted }: {
  tokenId: number; accountName: string; authMode: AccountAuth; target: MaintenanceTarget
  healthy: boolean; onClose: () => void; onSubmitted: () => void
}) {
  const [form] = Form.useForm<MaintenanceValues>()
  const [commands, setCommands] = useState<OperationCommand[]>()
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const sending = useRef(false)
  const action = Form.useWatch('action', form)
  const slMode = Form.useWatch('slMode', form)
  const tpMode = Form.useWatch('tpMode', form)
  const prepare = async () => {
    try {
      setCommands(maintenanceCommands(target, await form.validateFields(), newRequestId()))
      setError('')
    } catch (e) { setError(e instanceof Error ? e.message : '请检查操作内容') }
  }
  const submit = async () => {
    if (!commands || sending.current || sent || !healthy) return
    sending.current = true
    setBusy(true)
    try {
      // Saving must succeed before any network call; recovery retains every child ID.
      rememberSubmissions(tokenId, commands.map(command => ({ requestId: command.request_id, command, createdAt: new Date().toISOString() })))
      setSent(true)
      const results = await Promise.allSettled(commands.map(command => cfdApi(authMode).submit(tokenId, command)))
      if (results.some(result => result.status === 'rejected')) {
        setError('部分请求未取得回执。请求 ID 已保留，请在操作记录中查原结果；不会自动重复执行。')
      }
      onSubmitted()
    } catch (e) { setError(e instanceof Error ? e.message : '无法保存请求，尚未提交') }
    finally { sending.current = false; setBusy(false) }
  }
  return <Drawer title="人工维护 · 黄金 CFD" open onClose={onClose} width={560} destroyOnClose>
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Descriptions bordered size="small" column={1}>
        <Descriptions.Item label="执行账户">{accountName} · #{tokenId}</Descriptions.Item>
        <Descriptions.Item label="明确目标">{target.label}</Descriptions.Item>
        <Descriptions.Item label="目标 ID">{target.position_id || target.order_id || target.signal_id}</Descriptions.Item>
      </Descriptions>
      {!healthy && <Alert type="warning" showIcon message="券商快照已过期或不完整" description="先关闭此面板并刷新账户数据，待数据恢复后提交。" />}
      <Form form={form} layout="vertical" disabled={!!commands || sent} initialValues={{ action: target.order_id ? 'cancel' : 'reduce', percentage: 30, slMode: 'keep', tpMode: 'keep' }}>
        <Form.Item label="操作" name="action">
          <Radio.Group options={target.order_id ? [{ value: 'cancel', label: '撤销该挂单' }] : [
            { value: 'reduce', label: '减仓' }, { value: 'adjust', label: '调整保护' }, { value: 'full_close', label: '全平' },
          ]} />
        </Form.Item>
        {action === 'reduce' && <>
          <Form.Item label="请求减仓比例 (%)" name="percentage" rules={[{ required: true }]}><InputNumber min={0.01} max={100} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="breakeven" valuePropName="checked"><Checkbox>随后独立将止损移至入场价</Checkbox></Form.Item>
          <Alert type="info" message="减仓与保本分开记录" description="减仓明确失败也会继续保本；减仓结果未知时先等待核实。" />
        </>}
        {action === 'adjust' && <>
          <Form.Item label="止损" name="slMode"><Select options={[{ value: 'keep', label: '保持' }, { value: 'entry', label: '移至入场价' }, { value: 'price', label: '指定价格' }, { value: 'clear', label: '清除止损' }]} /></Form.Item>
          {slMode === 'price' && <Form.Item label="止损价格" name="slPrice" rules={[{ required: true }]}><InputNumber min={0.01} style={{ width: '100%' }} /></Form.Item>}
          <Form.Item label="止盈" name="tpMode"><Select options={[{ value: 'keep', label: '保留原生 TP 分档' }, { value: 'price', label: '指定目标价格' }, { value: 'clear', label: '清除止盈' }]} /></Form.Item>
          {tpMode === 'price' && <Form.Item label="止盈价格" name="tpPrice" rules={[{ required: true }]}><InputNumber min={0.01} style={{ width: '100%' }} /></Form.Item>}
        </>}
      </Form>
      <Alert type="info" showIcon message="以服务器固定方案与券商证据为准" description="正数减仓不足 0.01 手时升至 0.01 手，上限为剩余持仓，可导致最后一腿全平。服务器执行前固定每腿数量；当前持仓可能已被自动 TP 改变。" />
      {commands && <div>
        <Typography.Title level={5}>待提交的独立操作</Typography.Title>
        {commands.map(command => <Descriptions key={command.request_id} size="small" column={1} bordered style={{ marginBottom: 8 }}>
          <Descriptions.Item label="操作">{command.operation === 'reduce' ? `减仓 ${command.close_percentage}%` : command.operation === 'adjust' ? '调整保护' : command.operation === 'full_close' ? '全平' : '撤单'}</Descriptions.Item>
          <Descriptions.Item label="请求 ID"><Typography.Text copyable>{command.request_id}</Typography.Text></Descriptions.Item>
          <Descriptions.Item label="保护要求">{command.sl_to_entry ? '止损移至入场价' : command.sl_clear ? '清除止损' : command.sl_price ? `SL ${command.sl_price}` : '止损保持'}；{command.tp_clear ? '清除止盈' : command.tp_price ? `TP ${command.tp_price}` : 'TP 分档保持'}</Descriptions.Item>
        </Descriptions>)}
      </div>}
      {!!error && <Alert type="warning" showIcon message={error} />}
      {sent ? <Button type="primary" onClick={onClose}>查看固定方案与执行结果</Button>
        : commands ? <Space><Button onClick={() => setCommands(undefined)} disabled={busy}>返回修改</Button><Button type="primary" danger={action === 'full_close'} loading={busy} disabled={!healthy} onClick={() => void submit()}>确认提交到此账户</Button></Space>
        : <Button type="primary" disabled={!healthy} onClick={() => void prepare()}>检查操作要求</Button>}
    </Space>
  </Drawer>
}
